import { 
  Component, 
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  SimpleChanges,
  EventEmitter,
  Output,
  ViewChild,
  Inject,
  forwardRef
} from '@angular/core';

import { 
  FormGroup,
  FormControl,
  Validators 
} from '@angular/forms';

import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { isEqual, isNil } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil, mergeMap, shareReplay } from 'rxjs/operators';
import { DatexFormControl, validateControlOnChange, validateFormOnControlChange } from './models/datex-form-control';
import { TabItemModel, TabGroupModel } from './models/tab';
import { WidgetModel } from './models/widget';
import { 
  TextBoxModel, 
  NumberBoxModel, 
  SelectBoxModel, 
  ESelectBoxType,
  DateBoxModel, 
  CheckBoxModel, 
  TextModel, 
  LabelModel, 
  ButtonModel,
  SplitButtonModel,
  SeparatorModel,
  ImageModel,
  DrawModel,
  CodeBoxModel,
  ButtonStyles,
  ValueControlModel
} from './models/control';
import { Styles, ControlContainerStyles } from './models/style';
import { FieldModel } from './models/field';
import { FieldsetModel } from './models/fieldset';
import { ToolModel } from './models/tool';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { FootPrintPortalManager_ShellService, EModalSize, EToasterType, EToasterPosition } from './FootPrintPortalManager.shell.service';
import { FootPrintPortalManager_OperationService } from './FootPrintPortalManager.operation.service';
import { FootPrintPortalManager_DatasourceService } from './FootPrintPortalManager.datasource.index';
import { FootPrintPortalManager_FlowService } from './FootPrintPortalManager.flow.index';
import { FootPrintPortalManager_ReportService } from './FootPrintPortalManager.report.index';
import { FootPrintPortalManager_LocalizationService } from './FootPrintPortalManager.localization.service';
import { Language } from './localization.service';
import { $types } from './FootPrintPortalManager.types'

import { DockAppointments_dock_appointment_types_dd_singleComponent } from './DockAppointments.dock_appointment_types_dd_single.component'
import { DockAppointments_dock_appointment_statuses_singleComponent } from './DockAppointments.dock_appointment_statuses_single.component'
import { Carriers_carriers_dd_singleComponent } from './Carriers.carriers_dd_single.component'

@Component({
  standalone: true,
  imports: [
    SharedModule,
    forwardRef(() => DockAppointments_dock_appointment_types_dd_singleComponent),
    forwardRef(() => DockAppointments_dock_appointment_statuses_singleComponent),
    forwardRef(() => Carriers_carriers_dd_singleComponent),
  ],
  selector: 'FootPrintPortalManager-dock_appointment_editor',
  templateUrl: './FootPrintPortalManager.dock_appointment_editor.component.html'
})
export class FootPrintPortalManager_dock_appointment_editorComponent implements OnInit, OnDestroy, OnChanges {
  inParams: { dockAppointmentId: number, orderId?: number, loadContainerId?: number } = { dockAppointmentId: null, orderId: null, loadContainerId: null };
  //#region Inputs
  @Input('dockAppointmentId') set $inParams_dockAppointmentId(v: number) {
    this.inParams.dockAppointmentId = v;
  }
  get $inParams_dockAppointmentId(): number {
    return this.inParams.dockAppointmentId;
  }
  @Input('orderId') set $inParams_orderId(v: number) {
    this.inParams.orderId = v;
  }
  get $inParams_orderId(): number {
    return this.inParams.orderId;
  }
  @Input('loadContainerId') set $inParams_loadContainerId(v: number) {
    this.inParams.loadContainerId = v;
  }
  get $inParams_loadContainerId(): number {
    return this.inParams.loadContainerId;
  }
  //#endregion Inputs

  //#region Outputs
  @Output()
  $finish = new EventEmitter();
  @Output()
  $refreshEvent = new EventEmitter();
  outParams: { confirm?: boolean } = { confirm: null };
  //#endregion Outputs

  //#region title
  // Make it async so that it won't cause expressionChangedAfterItHasBeenCheckedError
  // The title is often meant to be shown from the parent (shell breadcrumb for example)
  // and often it will cause an expressionChangedAfterItHasBeenCheckedError because 
  // the parent has already been checked and the child now change something on the parent 
  // in dev, CD is run twice
  $titleChange = new EventEmitter<string>(true);
  private $_title: string;
  get title(): string {
    return this.$_title;
  }
  set title(t: string) {
    this.$_title = t;
    this.$titleChange.emit(this.$_title);
  }
  //#endregion title
  //#region Variables
  vars: { orders?: number[], appointmentType?: string, doorType?: string } = { };
  //#endregion
  entity: { Id?: number, AssignedOn?: string, CancelledOn?: string, CheckedInOn?: string, CompletedOn?: string, DriverLicense?: string, DriverName?: string, InProcessOn?: string, LookupCode?: string, Notes?: string, ReferenceNumber?: string, ScheduledArrival?: string, ScheduledDeparture?: string, TypeId?: number, VehicleLicense?: string, WarehouseId?: number, ScheduledLocation?: { Id?: number, Name?: string, WarehouseId?: number }, ScheduledCarrier?: { Id?: number, Name?: string }, ScheduledOwner?: { Id?: number, Name?: string }, ScheduledProject?: { Id?: number, Name?: string }, Status?: { Id?: number, Name?: string }, Items?: { Id?: number, ItemEntityId?: number, ItemEntityType?: string }[], AssignedLocation?: { Id?: number, Name?: string, WarehouseId?: number }, Warehouse?: { Id?: number, Name?: string, TimeZoneId?: string }, convertedScheduledArrival?: { id?: number, convertedDate?: string }, convertedScheduledDeparture?: { id?: number, convertedDate?: string }, convertedCheckInOn?: { id?: number, convertedDate?: string }, convertedAssignedOn?: { id?: number, convertedDate?: string }, convertedInProcessOn?: { id?: number, convertedDate?: string }, convertedCompletedOn?: { id?: number, convertedDate?: string }, convertedCancelledOn?: { id?: number, convertedDate?: string } };

  formGroup: FormGroup = new FormGroup({
    lookupcode: new DatexFormControl(null, { validators: [ Validators.required ], updateOn: 'blur' }),
    type: new DatexFormControl(null, { validators: [ Validators.required ], updateOn: 'blur' }),
    scheduled_arrival: new DatexFormControl(null, { validators: [ Validators.required ], updateOn: 'blur' }),
    status: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
    carrier: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
    reference_number: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
    notes: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
  });
  
  get valid(): boolean {
    return this.formGroup.valid;
  }

  toolbar = {
      on_save: new ToolModel(new ButtonModel('on_save', new ButtonStyles(['primary'], null), false, 'Save', 'ms-Icon ms-Icon--Save')
    ),
      cancel: new ToolModel(new ButtonModel('cancel', new ButtonStyles(['secondary'], null), false, 'Cancel', 'icon-ic_fluent_dismiss_circle_20_regular')
    ),
      on_delete: new ToolModel(new ButtonModel('on_delete', new ButtonStyles(['destructive'], null), false, 'Delete', 'ms-Icon ms-Icon--Delete')
    ),
      separator1: new ToolModel(new SeparatorModel(new Styles(null, null))
    ),
      print: new ToolModel(new ButtonModel('print', new ButtonStyles(null, null), false, ' ', 'ms-Icon ms-Icon--Print')
    ),
      attachments: new ToolModel(new ButtonModel('attachments', new ButtonStyles(null, null), false, ' ', 'ms-Icon ms-Icon--Attach')
    )
  };

  fields = {
    lookupcode: new FieldModel(new TextBoxModel(this.formGroup.controls['lookupcode'] as DatexFormControl, null, true, '')
, new ControlContainerStyles(null, null), 'Lookup', true)
,
    type: new FieldModel(new SelectBoxModel(
  this.formGroup.controls['type'] as DatexFormControl, 
  null, null,
  true, 
  '')
, new ControlContainerStyles(null, null), 'Type', true)
,
    scheduled_arrival: new FieldModel(new DateBoxModel(this.formGroup.controls['scheduled_arrival'] as DatexFormControl, null, false, '', 'datetime')
, new ControlContainerStyles(null, null), 'Planned Arrival', true)
,
    status: new FieldModel(new SelectBoxModel(
  this.formGroup.controls['status'] as DatexFormControl, 
  null, null,
  true, 
  '')
, new ControlContainerStyles(null, null), 'Status', false)
,
    carrier: new FieldModel(new SelectBoxModel(
  this.formGroup.controls['carrier'] as DatexFormControl, 
  null, null,
  false, 
  '')
, new ControlContainerStyles(null, null), 'Carrier', false)
,
    reference_number: new FieldModel(new TextBoxModel(this.formGroup.controls['reference_number'] as DatexFormControl, null, false, '')
, new ControlContainerStyles(null, null), 'Reference number', false)
,
    notes: new FieldModel(new TextBoxModel(this.formGroup.controls['notes'] as DatexFormControl, null, false, '')
, new ControlContainerStyles(null, null), 'Notes', false)
,
  };

  fieldsets = {
  newGroup1: new FieldsetModel('', true, false, true),
};


  //#region fields inParams
  get $fields_type_selector_inParams_option(): string {
    if (!this.entity) return null; 
    const $editor = this;
    const $utils = this.utils;
    const expr = $editor.vars.doorType;
    
    return expr;
  }

  //#endregion fields inParams

  $formGroupFieldValidationObservables = {
    lookupcode: this.fields.lookupcode.control.valueChanges
    ,
    type: this.fields.type.control.valueChanges
    ,
    scheduled_arrival: this.fields.scheduled_arrival.control.valueChanges
    ,
    status: this.fields.status.control.valueChanges
    ,
    carrier: this.fields.carrier.control.valueChanges
    ,
    reference_number: this.fields.reference_number.control.valueChanges
    ,
    notes: this.fields.notes.control.valueChanges
    ,
  }
  constructor(
    private utils: UtilsService,
    private settings: SettingsValuesService,
    private shell: FootPrintPortalManager_ShellService,
    private datasources: FootPrintPortalManager_DatasourceService,
    private flows: FootPrintPortalManager_FlowService,
    private reports: FootPrintPortalManager_ReportService,
    private localization: FootPrintPortalManager_LocalizationService,
    private operations: FootPrintPortalManager_OperationService,
    ) { 
    this.$subscribeFormControlValueChanges();
    
  }

  ngOnInit(): void {
    this.$checkRequiredInParams();
    if(!this.$hasMissingRequiredInParams) {
      this.$init();
    }
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$checkRequiredInParams();
      if(!this.$hasMissingRequiredInParams) {
        this.$init();
      }
    }
  }

  private $unsubscribe$ = new Subject();
  ngOnDestroy(): void {
    this.$unsubscribe$.next(null);
    this.$unsubscribe$.complete();
  }
  $missingRequiredInParams = [];
  get $hasMissingRequiredInParams(): boolean {
    return !!this.$missingRequiredInParams.length;
  }
  
  $checkRequiredInParams() {
    this.$missingRequiredInParams = [];
      if(isNil(this.inParams.dockAppointmentId)) {
        this.$missingRequiredInParams.push('dockAppointmentId');
      }
  }

  initialized = false;
  $hasDataLoaded = false;

  async $init() {
    this.title = 'Dock Appointment Editor';
    
    await this.on_init();
    await this.$dataLoad();
    this.initialized = true;
  }

  async $dataLoad() {
    const $editor = this;
    const $utils = this.utils;

    const dsParams = {
      dockAppointmentId:  $editor.inParams.dockAppointmentId 
    };

    const data = await this.datasources.FootPrintPortalManager.ds_dock_appointment_editor.get(dsParams);

    if (isNil(data.result)) {
      this.$hasDataLoaded = false;
      this.entity = null;
    } else {
      this.$hasDataLoaded = true;
      this.entity = data.result;
      await this.$dataLoaded();
    }
  }

  async $dataLoaded() {
    const $editor = this;
    const $utils = this.utils;
   
    (this.fields.lookupcode.control as TextBoxModel).reset($editor.entity.LookupCode);
    (this.fields.type.control as SelectBoxModel).reset($editor.entity.TypeId);
    (this.fields.scheduled_arrival.control as DateBoxModel).reset($editor.entity.convertedScheduledArrival?.convertedDate);
    (this.fields.status.control as SelectBoxModel).reset($editor.entity.Status?.Id);
    (this.fields.carrier.control as SelectBoxModel).reset($editor.entity.ScheduledCarrier?.Id);
    (this.fields.reference_number.control as TextBoxModel).reset($editor.entity.ReferenceNumber);
    (this.fields.notes.control as TextBoxModel).reset($editor.entity.Notes);

    await this.on_data_loaded();
  }

  refresh(
    skipParent = false,
    skipChildren = false,
    childToSkip: string = null) {
    if (this.$hasMissingRequiredInParams) {
      return Promise.resolve(null);
    }
    // up
    if (skipParent === false) {
      this.$refreshEvent.emit();
    }

    // self
    const result = this.$dataLoad();
    
    // children
    if (skipChildren === false) {
      this.$refreshChildren(childToSkip);
    }

    return result;
  }

  $refreshChildren(childToSkip: string) {
  }

  close() {
    this.$finish.emit();
  }

  openImageViewer(imageSource: string) {
    this.shell.openImageViewerDialog(imageSource);
  }
  
  private $subscribeFormControlValueChanges() {
    this.$formGroupFieldValidationObservables
      .lookupcode
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .type
      .pipe(
        takeUntil(this.$unsubscribe$)
      )
      .subscribe(() => {
        this.on_type_change();
      });
    this.$formGroupFieldValidationObservables
      .scheduled_arrival
      .pipe(
        takeUntil(this.$unsubscribe$)
      )
      .subscribe(() => {
        this.on_scheduled_arrival_change();
      });
    this.$formGroupFieldValidationObservables
      .status
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .carrier
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .reference_number
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .notes
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
  }

  //#region private flows
  on_save_clicked(event = null) {
    return this.on_save_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_save_clickedInternal(
    $editor: FootPrintPortalManager_dock_appointment_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  
  $editor.toolbar.on_save.control.readOnly = true;
  
  // Check required fields
  const allRequiredFieldHaveValue = $utils.isAllDefined(
      $editor.fields.lookupcode.control.value,
      $editor.fields.type.control.value,
      $editor.fields.scheduled_arrival.control.value
  );
  
  if (!allRequiredFieldHaveValue) {
      $editor.toolbar.on_save.control.readOnly = false;
      $shell.DockAppointments.openErrorDialog('Appointment Edit Error', 'Please enter data in all required fields.');
      return;
  }
  
  
  // Update dock appointment
  
  let warehouse = (await $datasources.DockAppointments.ds_get_warehouse_by_warehouseId.get({ warehouseId: $editor.entity.WarehouseId })).result;
  if ($utils.isDefined(warehouse)) {
  
      var timezone = warehouse.TimeZoneId;
  }
  
  let payload: any = {};
  
  
  if ($editor.fields.scheduled_arrival.control.isChanged) {
  
      let resultArrival = await $flows.DockAppointments.get_utc_date_by_timezone({
          dateTime: $editor.fields.scheduled_arrival.control.value,
          timezone: timezone
      });
  
      if ($utils.isDefined(resultArrival)) {
          var scheduledArrivalUtc = resultArrival.utcDate;
  
      }
  
      payload.ScheduledArrival = scheduledArrivalUtc;
  }
  
  
  
  if ($editor.fields.carrier.control.isChanged) {
      payload.ScheduledCarrierId = $editor.fields.carrier.control.value;
  }
  if ($editor.fields.reference_number.control.isChanged) {
      payload.ReferenceNumber = $editor.fields.reference_number.control.value;
  }
  
  if ($editor.fields.type.control.isChanged) {
      payload.TypeId = $editor.fields.type.control.value;
  }
  
  if ($editor.fields.notes.control.isChanged) {
      payload.Notes = $editor.fields.notes.control.value;
  }
  
  
  // Status change only available when appointment is in a completed status
  if ($editor.fields.status.control.isChanged){
      payload.StatusId = $editor.fields.status.control.value;
  }
  
  
  try {
  
      await $flows.Utilities.crud_update_flow({ entitySet: 'DockAppointments', id: $editor.inParams.dockAppointmentId, entity: payload });
  
  
  }
  
  catch (error) {
      $editor.toolbar.on_save.control.readOnly = false;
      $shell.DockAppointments.showErrorDetails('Save', 'Error on save.', error);
      throw error; // to prevent displayMode 
  
  }
  
  
  // Update dock appointment items
  let payloadItems: any = {};
  
  
  
  // Set confirmation outParam and close editor
  $editor.outParams.confirm = true;
  $editor.close();
  }
  on_type_change(event = null) {
    return this.on_type_changeInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_type_changeInternal(
    $editor: FootPrintPortalManager_dock_appointment_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  const appointmentType = (await $datasources.DockAppointments.ds_dock_appointment_type_by_typeId.get({
      typeId: $editor.fields.type.control.value
      })).result;
  
  if($utils.isDefined(appointmentType)){
      $editor.vars.appointmentType = appointmentType.Options;
  }
  
  
  }
  on_init(event = null) {
    return this.on_initInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_initInternal(
    $editor: FootPrintPortalManager_dock_appointment_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  // Set Date/Time Formats
  $editor.fields.scheduled_arrival.control.format = `${$settings.FootPrintPortalManager.DateFormat}, ${$settings.FootPrintPortalManager.TimeFormat.toUpperCase() == '24 HOUR' ? 'HH:mm' : 'LT'}`;
  
  
  
  }
  on_scheduled_arrival_change(event = null) {
    return this.on_scheduled_arrival_changeInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_scheduled_arrival_changeInternal(
    $editor: FootPrintPortalManager_dock_appointment_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  
  
  if ($editor.fields.scheduled_arrival.control.isChanged) {
      
  }
  
  
  }
  on_delete_clicked(event = null) {
    return this.on_delete_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_delete_clickedInternal(
    $editor: FootPrintPortalManager_dock_appointment_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  //O.Arias - 02/24/2023 - Initial Development
  
  // Set some default values to some variables
  let is_deletable = 'No';
  let assignment_exists = 'No';
  let message_box_context = 'Are you sure you want to delete appointment ' + $editor.entity.LookupCode + '?';
  
  // Gather dock appointment status
  const dockAppt = (await $datasources.DockAppointments.ds_get_dock_appointment_by_appointmentId.get({
      appointmentId: $editor.entity.Id
  })).result;
  
  // Check for valid statuses
  if ($utils.isDefined(dockAppt)) {
      const dockStatus = dockAppt.StatusId
      if (dockStatus == 0) { is_deletable = 'Yes' }; //Created
      if (dockStatus == 5) { is_deletable = 'Yes' }; //Cancelled
      if (dockStatus == 1) { is_deletable = 'In-Yard' };
      if (dockStatus == 2) { is_deletable = 'Door Assigned' };
      if (dockStatus == 3) { is_deletable = 'In-Process' };
      if (dockStatus == 4) { is_deletable = 'Completed' };
  }
  
  if (is_deletable == 'Yes') {
  
      //Check for existing items
      const dockAppointmentItems = (await $datasources.DockAppointments.ds_dock_appointment_items.get({ dockAppointmentId: $editor.entity.Id })).result;
  
      //Populate the additional context to the message box
      if ($utils.isDefined(dockAppointmentItems)) {
          assignment_exists = 'True';
          message_box_context = 'The appointment is already assigned! ' + message_box_context + ' All current assignments will be deleted!';
      };
  
      //Making sure the message box has data
      if ($utils.isDefined(message_box_context)) {
  
          const confirm = await $shell.DockAppointments.openConfirmationDialog('Delete Dock Appointment ' + $editor.entity.LookupCode, message_box_context, 'Yes', 'No');
          if (confirm) {
              try {
  
                  //Delete the dock appointment items
                  if ($utils.isDefined(dockAppointmentItems)) {
                      for (let item of dockAppointmentItems) {
                          let itemId = item.Id;
                          //Delete dock appointment item record
                          await $flows.Utilities.crud_delete_flow({ entitySet: 'DockAppointmentItems', id: itemId });
                      }
                  }
  
                  //Delete the dock appointment
                  await $flows.Utilities.crud_delete_flow({ entitySet: 'DockAppointments', id: $editor.entity.Id });
  
                  //Set the confirm output to true so it refreshes the caller
                  $editor.outParams.confirm = true;
                  //Close the editor
                  $editor.close();
  
              } catch (error) {
                  const errorMessage = $utils.isDefined(error?.error?.error) ? error?.error?.error.message : error;
                  const errorDetail = $utils.isDefined(error?.error?.error) ? error?.error?.error : error;
                  const errorDescription = `Dock Appointment ${$editor.entity.LookupCode} - ${errorMessage}`;
                  await $shell.DockAppointments.openErrorDialog('Cannot Delete Dock Appointment', 'An error occurred during the deletion of the dock appointment: ', [errorDescription], null, [{ message: errorDescription, detail: errorDetail }]);
              }
          }
      } else {
          await $shell.DockAppointments.openErrorDialog('Cannot Delete Dock Appointment', 'Unhandled Exception!');
          return;
      };
  
  }
  else {
      if (is_deletable !== 'Yes') {
          message_box_context = 'Dock appointment ' + $editor.entity.LookupCode + ' is in [' + is_deletable + '] status and cannot be deleted!';
      } else {
          message_box_context = 'Unhandled Exception!'
      };
      await $shell.DockAppointments.openErrorDialog('Cannot Delete Dock Appointment', message_box_context);
      return;
  };
  
  
  }
  on_data_loaded(event = null) {
    return this.on_data_loadedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_data_loadedInternal(
    $editor: FootPrintPortalManager_dock_appointment_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  // Pass the appointment type into the variable which is used as a filter on the dock door
  const appointmentType = (await $datasources.DockAppointments.ds_dock_appointment_type_by_typeId.get({
      typeId: $editor.fields.type.control.value
      })).result;
  
  if($utils.isDefined(appointmentType)){
      $editor.vars.appointmentType = appointmentType.Options;
  }
  
  
  
  
  var dockAppointmentStatus = $editor.entity.Status.Id;
  $editor.vars.orders = [];
  
  // Ignore building the order variable if the load container id parameter is passed in
  if (!$utils.isDefined($editor.inParams.loadContainerId)) {
  
      // Set orders variable to all assigned orders
      if ($utils.isDefined($editor.entity.Items)) {
  
          const orders = $editor.entity.Items;
          for (const order of orders) {
  
              if ($utils.isDefined(order.ItemEntityId)) {
                  $editor.vars.orders.push(order.ItemEntityId);
              }
  
          }
  
      }
  
  
  
  };
  
  // Set status field to read only by default since the status can only manually change when the appointment is a in a completed status
  $editor.fields.status.control.readOnly = true;
  
  // Change label of dock door to scheduled vs assigned based on state of the appointment
  
  if ($editor.entity.Status.Id == 0) // Open
  {
    
      // Visible
      $editor.toolbar.cancel.hidden = false;
      $editor.toolbar.on_delete.hidden = false;
  
  }
  
  
  
  else if ($editor.entity.Status.Id == 1) {  // In-Yard
  
  
  
      // Visible
      $editor.toolbar.cancel.hidden = false;
      $editor.toolbar.on_delete.hidden = false;
  
  
      $editor.fields.scheduled_arrival.control.readOnly = true;
  
  
  }
  
  else if ($editor.entity.Status.Id == 2) {  // Door Assigned
  
  
  
      // Hidden
      $editor.toolbar.on_delete.hidden = true;
  
      // Visible
      $editor.toolbar.cancel.hidden = false;
  
      $editor.fields.scheduled_arrival.control.readOnly = true;
  
  
  }
  
  else if ($editor.entity.Status.Id == 3) {  // In process
  
      // Hidden
      $editor.toolbar.on_delete.hidden = true;
  
      // Visible
      $editor.toolbar.cancel.hidden = false;
  
  
      // Dates
  
      $editor.fields.scheduled_arrival.control.readOnly = true;
  
  
  
  
  }
  
  else if ($editor.entity.Status.Id == 4) {  // Completed
  
      // Allow status field to be changed
      $editor.fields.status.control.readOnly = false;
  
      // Hidden
      $editor.toolbar.cancel.hidden = true;
      $editor.toolbar.on_delete.hidden = true;
  
  
      // Readonly
  
      $editor.fields.scheduled_arrival.control.readOnly = true;
  
      $editor.fields.carrier.control.readOnly = true;
  
      // Dates
  
  
      var arivalDate = $utils.isDefined($editor.entity.convertedInProcessOn?.convertedDate) ? $editor.entity.convertedInProcessOn?.convertedDate :
          ($utils.isDefined($editor.entity.convertedAssignedOn?.convertedDate) ? $editor.entity.convertedAssignedOn?.convertedDate :
              ($utils.isDefined($editor.entity.convertedCheckInOn?.convertedDate) ? $editor.entity.convertedCheckInOn?.convertedDate : null))
  
      var arrivalLabel = $utils.isDefined($editor.entity.InProcessOn) ? 'In Process On' :
          ($utils.isDefined($editor.entity.AssignedOn) ? 'Assigned On' :
              ($utils.isDefined($editor.entity.CheckedInOn) ? 'Checked In On' : ''))
  
      $editor.fields.scheduled_arrival.label = $utils.isDefined(arrivalLabel) ? arrivalLabel : 'Scheduled Arrival';
      $editor.fields.scheduled_arrival.control.value = $utils.isDefined(arivalDate) ? arivalDate : $editor.entity.convertedScheduledArrival.convertedDate;
  
  
  }
  
  else if ($editor.entity.Status.Id == 5) {  // Cancelled
  
      // Hidden
      $editor.toolbar.cancel.hidden = true;
      $editor.toolbar.on_delete.hidden = true;
  
  
      // Readonly
  
      $editor.fields.scheduled_arrival.control.readOnly = true;
  
      $editor.fields.carrier.control.readOnly = true;
  
      // Dates
  
      $editor.fields.scheduled_arrival.hidden = true;
  
  
  }
  
  
  }
  on_cancel_clicked(event = null) {
    return this.on_cancel_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_cancel_clickedInternal(
    $editor: FootPrintPortalManager_dock_appointment_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  
  $editor.toolbar.cancel.control.readOnly = true;
  
  const dialogResult = await $shell.FootPrintPortalManager.opendock_appointment_cancel_options_formDialog({});
  var userConfirmed = dialogResult.confirm;
  var reasonCodeId = dialogResult.reasonCodeId;
  
  
  
  if ($utils.isDefined(userConfirmed) && userConfirmed === false) {
  
      $editor.toolbar.cancel.control.readOnly = false;
      return;
  }
  
  if (userConfirmed) {
      try {
  
          // Cannot use native actions as timezone is conflicting
          const user = (await $flows.Utilities.get_username_flow({})).userName;
  
          if ($utils.isDefined(reasonCodeId)) {
              // const result = await $apis.DockAppointments.FootPrintAPI.DockAppointments.CancelDockAppointment({
              //     DockAppointmentId: $editor.entity.Id,
              //     UserName: user,
              //     ReasonCodeId: reasonCodeId
              // });
              var resultUtc = $utils.date.add(new Date().getTimezoneOffset(), "minute", $utils.date.now());
  
  
              let payload: any = {};
  
  
              payload.CancelledBy = user,
                  payload.StatusId = 5,
                  payload.CancelledOn = resultUtc,
                  payload.CancelledReasonCodeId = reasonCodeId
  
              await $flows.Utilities.crud_update_flow({ entitySet: 'DockAppointments', id: $editor.entity.Id, entity: payload });
          }
  
          // Remove all orders asscociated to the appointment
          const orders = $editor.entity.Items;
          for (const order of orders) {
  
              if ($utils.isDefined(order.ItemEntityId)) {
  
                  const dissociateOrderRequest = (await $flows.DockAppointments.diassociate_orders_from_dock_appointment_flow({
                      dockAppointmentId: $editor.inParams.dockAppointmentId,
                      orderId: order.ItemEntityId
                  }));
              }
  
          }
  
  
          // Set confirmation outParam and close editor
          $editor.outParams.confirm = true;
          $editor.close();
  
  
      } catch (error) {
          $editor.toolbar.cancel.control.readOnly = false;
  
          const errorMessage = $utils.isDefined(error?.error?.error) ? error?.error?.error.message : error;
          const errorDetail = $utils.isDefined(error?.error?.error) ? error?.error?.error : error;
          const errorDescription = `Appointment ${$editor.entity.LookupCode} - ${errorMessage}`;
          await $shell.DockAppointments.openErrorDialog('Assigning door on appointment error', 'An error occurred during the door assignment of the appointment.', [errorDescription], null, [{ message: errorDescription, detail: errorDetail }]);
  
  
  
      }
  }
  
  }
  on_print_clicked(event = null) {
    return this.on_print_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_print_clickedInternal(
    $editor: FootPrintPortalManager_dock_appointment_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  
  await $shell.FootPrintPortalManager.openInfoDialog('Not yet implemented', 'The print functionality for this screen has not yet been implemented.');
  }
  on_attachments_clicked(event = null) {
    return this.on_attachments_clickedInternal(
      this,
  this.shell,
      this.datasources,
      this.flows,
      this.reports,
      this.settings,
      this.operations,
      this.utils,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_attachments_clickedInternal(
    $editor: FootPrintPortalManager_dock_appointment_editorComponent,
  
    $shell: FootPrintPortalManager_ShellService,
    $datasources: FootPrintPortalManager_DatasourceService,
    $flows: FootPrintPortalManager_FlowService,
    $reports: FootPrintPortalManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootPrintPortalManager_OperationService,
    $utils: UtilsService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootPrintPortalManager_LocalizationService,
    $event: any
  ) {
  await $shell.FootPrintPortalManager.openentity_attachments_gridDialog({ entityType: 'DockAppointment', entityKeys: [{ name: 'Id', value: $editor.entity.Id }]});
  }
  //#endregion private flows
}
